/**
 * Configuración del sistema que usa el reproductor de video (`<VideoPlayer>`).
 *
 * Sale de `GET /config-options/video-optimization`, que puede leer CUALQUIER usuario
 * autenticado (cliente, aseador, admin): el reproductor lo necesita para saber con qué
 * calidad abrir un video cuando la persona todavía no eligió una. Solo se usan los campos del
 * reproductor; el resto (bitrate, resolución máxima, etc.) es de la pestaña del admin.
 *
 * Mismo patrón que `getImageOptimizationConfig`: `fetch` directo (un `apiFetch` mostraría un
 * toast de error por algo que no es accionable para el usuario), caché de 5 minutos en
 * `sessionStorage` y tolerancia total a fallos (si algo sale mal, la calidad predeterminada es
 * «baja», la que ahorra datos).
 */
import { API_BASE_URL } from "../services/apiClient";
import { leerConexion } from "./conexion";

export type CalidadVideo = "baja" | "alta";

export interface VideoPlayerConfig {
  /** El admin tiene encendida la generación de la versión liviana. */
  previewEnabled: boolean;
  /** Lado más corto (px) de la versión liviana: 240..720 (480 = 854x480 en horizontal, 480x854 en vertical). */
  previewHeight: number;
  /** Calidad con la que se abre un video cuando la persona no eligió una. */
  playerDefaultQuality: CalidadVideo;
}

/** Defaults: coinciden con los del backend (`VIDEO_OPT_DEFAULTS`). */
export const VIDEO_PLAYER_DEFAULTS: VideoPlayerConfig = {
  previewEnabled: true,
  previewHeight: 480,
  playerDefaultQuality: "baja",
};

const CACHE_KEY = "videoPlayerConfig";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min
/** Tras un fallo no se vuelve a pedir durante este tiempo (no martillar un endpoint caído). */
const REINTENTO_TRAS_FALLO_MS = 60 * 1000;

interface CachedConfig {
  data: VideoPlayerConfig;
  at: number;
}

let enVuelo: Promise<VideoPlayerConfig> | null = null;
let falloEn = 0;

/** Normaliza lo que contesta el backend: cualquier campo ausente o inválido cae al default. */
export function normalizarConfig(raw: unknown): VideoPlayerConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const alto = Number(r.previewHeight);
  return {
    previewEnabled:
      typeof r.previewEnabled === "boolean"
        ? r.previewEnabled
        : VIDEO_PLAYER_DEFAULTS.previewEnabled,
    previewHeight:
      Number.isFinite(alto) && alto >= 240 && alto <= 720
        ? Math.round(alto)
        : VIDEO_PLAYER_DEFAULTS.previewHeight,
    playerDefaultQuality:
      r.playerDefaultQuality === "alta" || r.playerDefaultQuality === "baja"
        ? r.playerDefaultQuality
        : VIDEO_PLAYER_DEFAULTS.playerDefaultQuality,
  };
}

/** Lee la configuración de la caché de `sessionStorage` si está vigente; `null` si no. */
export function leerVideoPlayerConfigEnCache(): VideoPlayerConfig | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedConfig;
    if (cached && cached.data && Date.now() - cached.at < CACHE_TTL_MS) {
      return normalizarConfig(cached.data);
    }
  } catch {
    // caché corrupta o sessionStorage bloqueado: se pide de nuevo
  }
  return null;
}

/**
 * Configuración del reproductor. Nunca lanza ni rechaza: ante cualquier fallo devuelve los
 * defaults (calidad «baja»). `force=true` se salta la caché.
 */
export async function getVideoPlayerConfig(force = false): Promise<VideoPlayerConfig> {
  if (!force) {
    const cache = leerVideoPlayerConfigEnCache();
    if (cache) return cache;
    // Un fallo reciente: no se insiste hasta que pase un minuto.
    if (falloEn && Date.now() - falloEn < REINTENTO_TRAS_FALLO_MS) return VIDEO_PLAYER_DEFAULTS;
    // Varios reproductores en pantalla (un carrusel) comparten UNA sola petición.
    if (enVuelo) return enVuelo;
  }

  const pedir = async (): Promise<VideoPlayerConfig> => {
    let data: VideoPlayerConfig | null = null;
    try {
      // Fetch directo (no apiFetch): sin toast si falla. La sesión viaja por la cookie
      // HttpOnly que agrega el interceptor global.
      const r = await fetch(`${API_BASE_URL}/config-options/video-optimization`);
      if (r.ok) {
        const body = (await r.json()) as { data?: unknown } | null;
        if (body && body.data) data = normalizarConfig(body.data);
      }
    } catch {
      data = null;
    }
    if (!data) {
      falloEn = Date.now();
      return VIDEO_PLAYER_DEFAULTS;
    }
    falloEn = 0;
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data, at: Date.now() } satisfies CachedConfig),
      );
    } catch {
      // sessionStorage no disponible (Safari privado, cuota)
    }
    return data;
  };

  const p = pedir().finally(() => {
    if (enVuelo === p) enVuelo = null;
  });
  enVuelo = p;
  return p;
}

/** Limpia la caché: usarlo tras guardar una nueva configuración. */
export function invalidateVideoPlayerConfig(): void {
  falloEn = 0;
  enVuelo = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* noop */
  }
}

// ── Preferencia de calidad de la persona ────────────────────────────────────────────────────

/** Clave de localStorage de la calidad que eligió la persona (se recuerda en este dispositivo). */
export const CLAVE_CALIDAD_VIDEO = "af_video_calidad";

/** Evento de ventana con que un reproductor avisa a los demás de que la persona cambió la calidad. */
export const EVENTO_CALIDAD_VIDEO = "af-video-calidad";

/** Calidad guardada por la persona, o `null` si no eligió ninguna (o el almacenamiento falla). */
export function leerCalidadGuardada(): CalidadVideo | null {
  try {
    const v = localStorage.getItem(CLAVE_CALIDAD_VIDEO);
    return v === "baja" || v === "alta" ? v : null;
  } catch {
    return null;
  }
}

/** Guarda la elección y avisa a los demás reproductores montados. Nunca lanza. */
export function guardarCalidad(calidad: CalidadVideo): void {
  try {
    localStorage.setItem(CLAVE_CALIDAD_VIDEO, calidad);
  } catch {
    /* modo privado o almacenamiento bloqueado: la elección vale solo para este reproductor */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_CALIDAD_VIDEO, { detail: calidad }));
  } catch {
    /* sin window (SSR) */
  }
}

/** ¿El navegador pidió ahorrar datos (Data Saver / modo de bajo consumo de datos)? */
export function pideAhorroDeDatos(): boolean {
  return leerConexion()?.saveData === true;
}

/**
 * Calidad con la que abre un reproductor que tiene versión liviana: lo que la persona guardó;
 * si no hay nada, «baja» cuando el navegador pide ahorrar datos; si no, la predeterminada del
 * sistema (o «baja» mientras la configuración no ha cargado).
 */
export function calidadInicial(config: VideoPlayerConfig | null): CalidadVideo {
  return (
    leerCalidadGuardada() ??
    (pideAhorroDeDatos() ? "baja" : (config?.playerDefaultQuality ?? "baja"))
  );
}
